'use client';

import { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { SavedSearch } from '@/types/customer';
import { PortalLayout } from '../PortalLayout';
import { Card } from '../ui/Card';

export function MySavedSearches() {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSearches = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      setLoading(true);
      const data = await client.getSavedSearches();
      setSearches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[MySavedSearches] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [getCustomerClient]);

  useEffect(() => { if (isAuthenticated) fetchSearches(); }, [isAuthenticated, fetchSearches]);

  const handleDelete = async (id: string) => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      await client.deleteSavedSearch(id);
      setSearches(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.warn('[MySavedSearches] Failed to delete:', err);
    }
  };

  const formatFilters = (filters: Record<string, unknown>): string => {
    const parts: string[] = [];
    if (filters.propertyType) parts.push(String(filters.propertyType));
    if (filters.minPrice || filters.maxPrice) {
      const min = filters.minPrice ? `${filters.minPrice}` : '0';
      const max = filters.maxPrice ? `${filters.maxPrice}` : '∞';
      parts.push(`${min} - ${max}`);
    }
    if (filters.bedrooms) parts.push(`${filters.bedrooms}+ beds`);
    if (filters.location) parts.push(String(filters.location));
    return parts.length > 0 ? parts.join(' · ') : 'All properties';
  };

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved Searches</h1>
          <p className="text-gray-600 mt-1">Manage your saved property searches and alerts</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : searches.length === 0 ? (
          <Card className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No saved searches</h3>
            <p className="text-gray-500 text-sm">Save a search from the properties page to get notified of new matches</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {searches.map(search => (
              <Card key={search.id} className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{search.name}</h3>
                    <p className="text-sm text-gray-600 mt-0.5">{formatFilters(search.filters)}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {search.matchCount != null && <span>{search.matchCount} matches</span>}
                      {search.alertEnabled && (
                        <span className="flex items-center gap-1 text-blue-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>
                          Alerts {search.alertFrequency?.toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(search.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Delete search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
